/* eslint-disable */
import React, { useRef, useState } from 'react';
import {
  Download,
  FileText,
  Pencil,
  ArrowLeft,
  Upload,
  FileUp,
  Building2,
  MapPin,
  ArrowRight,
  Plus,
} from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import appConfig from '../../config';
import Swal from 'sweetalert2';
import { adminCorrectContract, updateContract, uploadFile } from 'src/helper/backend_helper';
import { OwnerExport } from './ownerExport';

const stringifyForEdit = (value) => JSON.stringify(value || {}, null, 2);
const stringifyArrayForEdit = (value) => JSON.stringify(Array.isArray(value) ? value : [], null, 2);
const getSignedMode = (contract) => contract?.signedContractMode || contract?.selectedContractMode || 'standard';

const buildAdminEditState = (contract) => ({
  signedContractMode: getSignedMode(contract),
  name: contract?.name || '',
  language: contract?.language || '',
  contractDuration: contract?.contractDuration || '',
  percentage: contract?.percentage ?? '',
  terminationNotice: contract?.terminationNotice || '',
  companyInfo: stringifyForEdit(contract?.companyInfo),
  companyOwner: stringifyForEdit(contract?.companyOwner),
  privateOwner: stringifyForEdit(contract?.privateOwner),
  bankInfo: stringifyForEdit(contract?.bankInfo),
  properties: stringifyArrayForEdit(contract?.properties),
  note: contract?.adminCorrection?.note || '',
});

const parseJsonForAdminEdit = (label, value) => {
  try {
    return JSON.parse(value || '{}');
  } catch (error) {
    throw new Error(label + ' is not valid JSON');
  }
};

export default function OwnerDetail() {
  const location = useLocation();
  const initialData = location.state?.data || {};
  const [data, setData] = useState(initialData);

  const [activeTab, setActiveTab] = useState('company');
  const [ownerNotes, setOwnerNotes] = useState(data?.ownerNotes || '');
  const [contractFiles, setContractFiles] = useState(data?.signatureFile || []);
  const [loading, setLoading] = useState(false);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [savingAdminEdit, setSavingAdminEdit] = useState(false);
  const [adminEdit, setAdminEdit] = useState(() => buildAdminEditState(initialData));
  const contractUploadInputRef = useRef(null);
  const navigate = useNavigate();

  console.log(data);
  const ownerInfo = data?.ownerType === 'private' ? data?.privateOwner : data?.companyOwner;

  const handleDownloadContract = (file) => {
    const fileUrl = `${appConfig.FILE_URL}${file}`;
    window.open(fileUrl, '_blank');
  };

  const getSelectedContractDetails = () => {
    const mode = data?.selectedContractMode === 'allInclusive' && data?.allInclusiveContractTemplate ? 'allInclusive' : 'standard';
    return {
      mode,
      label: mode === 'allInclusive' ? 'All Inclusive' : 'Standard',
      template: mode === 'allInclusive' ? data?.allInclusiveContractTemplate?.template : data?.standardContractTemplate?.template || data?.contractTemplate?.template,
      percentage: mode === 'allInclusive' ? data?.allInclusivePercentage : data?.standardPercentage ?? data?.percentage,
      contractDuration: mode === 'allInclusive' ? data?.allInclusiveContractDuration : data?.standardContractDuration || data?.contractDuration,
      terminationNotice: mode === 'allInclusive' ? data?.allInclusiveTerminationNotice : data?.standardTerminationNotice || data?.terminationNotice,
    };
  };

  const renderOriginalContractHtml = () => {
    const details = getSelectedContractDetails();
    let template = details.template || '';
    const owner = data?.ownerType === 'private' ? data?.privateOwner : data?.companyOwner;
    const signature = data?.paymentInfo?.ownerSignature
      ? '<img alt="Owner signature" src="' + data.paymentInfo.ownerSignature + '" style="width:260px;max-height:120px;object-fit:contain;display:block;margin-top:12px;" />'
      : 'N/A';
    const ownerHtml = data?.ownerType === 'private'
      ? '<strong>' + (owner?.firstName || '') + ' ' + (owner?.lastName || '') + '</strong><br/>CF: ' + (owner?.fiscalCode || '') + '<br/>Email: ' + (owner?.email || '')
      : '<strong>' + (data?.companyInfo?.companyName || '') + '</strong><br/>P.IVA: ' + (data?.companyInfo?.vatNumber || '') + '<br/>Legale rappresentante: ' + (owner?.firstName || '') + ' ' + (owner?.lastName || '');
    const propertyHtml = (data?.properties || []).map((property, index) => {
      const c = property?.cadastralInfo || {};
      return '<div style="margin-bottom:10px"><strong>Property ' + (index + 1) + ': ' + (property?.name || '') + '</strong><br/>' + (property?.address || '') + ' ' + (property?.streetNumber || '') + ', ' + (property?.city || '') + '<br/>Foglio: ' + (c.sheet || '') + ' - Particella: ' + (c.particle || '') + ' - Subalterno: ' + (c.subordinate || '') + ' - Categoria: ' + (c.category || '') + ' - Rendita: ' + (c.cadastralIncome || '') + '</div>';
    }).join('');
    const bankHtml = 'Account Holder: ' + (data?.bankInfo?.accountHolder || '') + '<br/>Bank: ' + (data?.bankInfo?.bank || '') + '<br/>IBAN: ' + (data?.bankInfo?.iban || '');
    template = template.replace(/\[OWNER\]/g, ownerHtml);
    template = template.replace(/\[PROPERTIES\]/g, propertyHtml);
    template = template.replace(/\[BANKDETAILS\]/g, bankHtml);
    template = template.replace(/\[COMMISSION%\]/g, (details.percentage || 'N/A') + '%');
    template = template.replace(/\[CONTRACTDURATION\]/g, details.contractDuration || 'N/A');
    template = template.replace(/\[CANCELLATIONNOTICE\]/g, details.terminationNotice || 'N/A');
    template = template.replace(/\[DATE\]/g, new Date().toLocaleDateString());
    template = template.replace(/\[SIGNATURESAMPLE\]/g, signature);
    if (!template.includes(signature)) {
      template += '<div style="margin-top:32px;page-break-inside:avoid"><h3>Firma proprietario</h3>' + signature + '</div>';
    }
    return '<div style="font-family:Arial,sans-serif;line-height:1.45;color:#111;padding:24px;max-width:800px;margin:0 auto"><h2>Contract ' + details.label + '</h2>' + template + '</div>';
  };

  const handleDownloadOriginalPdf = async () => {
    const details = getSelectedContractDetails();
    if (!details.template) {
      Swal.fire({ icon: 'error', title: 'Missing template', text: 'No original template is available for this contract mode.' });
      return;
    }
    const html2pdf = (await import('html2pdf.js')).default;
    const container = document.createElement('div');
    container.innerHTML = renderOriginalContractHtml();
    await html2pdf().set({
      margin: 10,
      filename: (data?.name || 'contract') + '-' + details.mode + '-original.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
    }).from(container).save();
  };


  const handleUploadAdditionalContractClick = () => {
    contractUploadInputRef.current?.click();
  };

  const handleAdditionalContractUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setUploadingContract(true);
      const formData = new FormData();
      formData.append('file', file);
      const response = await uploadFile(formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const fileName = response?.data?.data?.fileName || response?.data?.fileName || response?.data?.data;

      if (!fileName) {
        throw new Error('Upload completed without returning a file name');
      }

      // Append the uploaded contract copy so previously stored signed contracts remain untouched.
      const nextFiles = [...contractFiles, fileName];
      await updateContract(data?._id, { signatureFile: nextFiles });
      setContractFiles(nextFiles);
      if (data) data.signatureFile = nextFiles;

      Swal.fire({ icon: 'success', title: 'Contract version uploaded successfully' });
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Error', text: 'Failed to upload contract version' });
    } finally {
      setUploadingContract(false);
      if (event.target) event.target.value = '';
    }
  };

  const updateAdminEditField = (field, value) => {
    setAdminEdit((prev) => ({ ...prev, [field]: value }));
  };

  const handleEnableEditMode = async () => {
    const confirmed = await Swal.fire({
      icon: 'warning',
      title: 'NON dovresti modificare nulla',
      text: 'Usa questa modalità solo per correggere errori amministrativi. I dati originali firmati resteranno salvati nello storico del contratto.',
      showCancelButton: true,
      confirmButtonText: 'Enable edit mode',
      cancelButtonText: 'Cancel',
    });

    if (confirmed.isConfirmed) {
      setAdminEdit(buildAdminEditState(data));
      setEditMode(true);
    }
  };

  const handleSaveAdminCorrection = async () => {
    try {
      setSavingAdminEdit(true);
      const companyInfo = parseJsonForAdminEdit('Company info', adminEdit.companyInfo);
      const companyOwner = parseJsonForAdminEdit('Company owner', adminEdit.companyOwner);
      const privateOwner = parseJsonForAdminEdit('Private owner', adminEdit.privateOwner);
      const bankInfo = parseJsonForAdminEdit('Bank info', adminEdit.bankInfo);
      const properties = parseJsonForAdminEdit('Properties', adminEdit.properties);

      if (!Array.isArray(properties)) {
        throw new Error('Properties must be a JSON array');
      }

      // Admin corrections are intentionally saved through a dedicated endpoint so the original signed payload stays recoverable.
      const payload = {
        note: adminEdit.note,
        contract: {
          signedContractMode: adminEdit.signedContractMode,
          name: adminEdit.name,
          language: adminEdit.language,
          contractDuration: adminEdit.contractDuration,
          percentage: adminEdit.percentage === '' ? undefined : Number(adminEdit.percentage),
          terminationNotice: adminEdit.terminationNotice,
          companyInfo,
          companyOwner,
          privateOwner,
          bankInfo,
        },
        properties,
      };

      const response = await adminCorrectContract(data?._id, payload, { headers: { 'Content-Type': 'application/json' } });
      const corrected = response?.data?.data;
      if (corrected) {
        setData(corrected);
        setAdminEdit(buildAdminEditState(corrected));
      }
      setEditMode(false);
      Swal.fire({ icon: 'success', title: 'Admin correction saved' });
    } catch (error) {
      console.error(error);
      Swal.fire({ icon: 'error', title: 'Correction not saved', text: error?.message || 'Failed to save correction' });
    } finally {
      setSavingAdminEdit(false);
    }
  };

  const handleSaveNotes = async () => {
    try {
      setLoading(true);

      const headers = {
        headers: {
          headers: { 'Content-Type': 'application/json' },
        },
      };

      const submitData = {
        ownerNotes,
      };
      console.log(submitData);
      await updateContract(data?._id, submitData);
      Swal.fire({
        icon: 'success',
        title: 'Owner notes updated successfully',
      });
      setLoading(false);
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'Failed to update',
      });
    }
  };

  console.log(data);
  const handleExport = () => {
    const ownerName = data?.ownerType === 'company' ? `${data?.companyInfo?.companyName} (${data?.name})` : data?.name;

    OwnerExport(data, `${ownerName}.xlsx`);
  };

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-2xl font-semibold">
          <FileText className="text-purple-600" />
          {data?.name}
        </div>
        <a onClick={() => navigate(-1)} className="text-sm text-blue-600 hover:underline cursor-pointer">
          ← Back
        </a>
      </div>

      <div className="flex justify-end mb-4">
        <button
          className="flex items-center gap-2 px-4 py-2 border rounded-md text-sm hover:bg-gray-100"
          onClick={handleExport}
        >
          <Upload size={16} /> Export as CSV
        </button>
      </div>

      <div className="bg-white border rounded-lg shadow-sm p-6">
        <div className="flex items-center gap-2 text-lg font-semibold mb-2">
          <FileText className="text-purple-600" /> Contract Details
        </div>
        <p className="text-sm text-gray-500 mb-4">Basic contract information</p>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div>
            <p className="text-sm text-gray-500">Contract ID</p>
            <p className="font-medium">{data?._id}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Status</p>
            <p className="flex items-center gap-1">Active</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Duration</p>
            <p className="flex items-center gap-1">{data?.contractDuration} weeks</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Cancellation Notice</p>
            <p>{data?.terminationNotice} days</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Signed</p>
            <p className="flex items-center gap-1">{new Date(data?.createdAt).toLocaleDateString()}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Language</p>
            <p className="flex items-center gap-1">{data?.language}</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Management Fee</p>
            <p className="flex items-center gap-1">{data?.percentage}%</p>
          </div>

          <div>
            <p className="text-sm text-gray-500">Signed Contract Mode</p>
            <p className="flex items-center gap-1 font-semibold">
              {getSignedMode(data) === 'allInclusive' ? 'All Inclusive' : 'Standard'}
            </p>
          </div>
        </div>
        <div className="mt-6">
          <button
            onClick={handleDownloadOriginalPdf}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
          >
            <Download size={16} /> Download Original PDF
          </button>
        </div>
        <input
          ref={contractUploadInputRef}
          type="file"
          className="hidden"
          accept=".pdf,.doc,.docx,image/*"
          onChange={handleAdditionalContractUpload}
        />
        <div className="mt-6 flex flex-col gap-3">
          {contractFiles.map((file, index) => (
            <div key={index} className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => handleDownloadContract(file)}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                <Download size={16} /> Download Signed Contract {index + 1}
              </button>
              <button
                onClick={handleUploadAdditionalContractClick}
                disabled={uploadingContract}
                className="flex items-center gap-2 px-4 py-2 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-60"
              >
                <FileUp size={16} /> {uploadingContract ? 'Uploading...' : 'Upload Another Version'}
              </button>
            </div>
          ))}
          {contractFiles.length === 0 && (
            <button
              onClick={handleUploadAdditionalContractClick}
              disabled={uploadingContract}
              className="flex items-center gap-2 px-4 py-2 border border-blue-200 text-blue-700 rounded-md hover:bg-blue-50 disabled:opacity-60 w-fit"
            >
              <FileUp size={16} /> {uploadingContract ? 'Uploading...' : 'Upload Contract Version'}
            </button>
          )}
        </div>
      </div>

      {data?.ownerType === 'company' ? (
        <div className="bg-white border rounded-lg shadow-sm mt-5">
          <div className="flex items-center gap-2 text-2xl font-bold p-5 mb-2">
            <FileText className="text-purple-600" /> Company Information
          </div>

          <div className="bg-gray-100 p-1 rounded-lg flex">
            <div
              onClick={() => setActiveTab('company')}
              className={`flex-1 text-center py-2 cursor-pointer rounded-lg transition 
          ${activeTab === 'company' ? 'bg-white text-black font-semibold' : 'text-gray-500'}`}
            >
              Company Details
            </div>
            <div
              onClick={() => setActiveTab('legal')}
              className={`flex-1 text-center py-2 cursor-pointer rounded-lg transition 
          ${activeTab === 'legal' ? 'bg-white text-black font-semibold' : 'text-gray-500'}`}
            >
              Legal Representative
            </div>
          </div>

          {/* {activeTab === "company" && (
          <div className="p-6">
            <div className="flex items-center gap-2 text-lg font-semibold mb-2">
              <FileText className="text-purple-600" /> Contract Details
            </div>
            <p className="text-sm text-gray-500 mb-4">Basic contract information</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-gray-500">Contract ID</p>
                <p className="font-medium">680064046d42c080d71422bb</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Status</p>
                <p className="flex items-center gap-1">
                  <Pencil size={14} /> Active
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Duration</p>
                <p className="flex items-center gap-1">
                  <Pencil size={14} /> 24 months
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Cancellation Notice</p>
                <p>30 days</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Signed</p>
                <p className="flex items-center gap-1">
                  <Pencil size={14} /> 15/04/2025
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Language</p>
                <p className="flex items-center gap-1">
                  <Pencil size={14} /> en
                </p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Management Fee</p>
                <p className="flex items-center gap-1">
                  <Pencil size={14} /> 15%
                </p>
              </div>
            </div>

            <div className="mt-6">
              <button className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                <Download size={16} /> Download Signed Contract
              </button>
            </div>
          </div>
        )} */}
          {activeTab === 'company' && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Company Info Fields */}
                <div>
                  <p className="text-sm text-gray-500">Name</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.companyName}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Type</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.companyType}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">VAT Number</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.vatNumber}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Fiscal Code</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.fiscalCode}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">REA Number</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.reaNumber}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Tax Status</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.taxStatus}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Email</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.email}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">PEC</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.pec}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Phone</p>
                  <p className="flex items-center gap-2 font-medium">
                    {data?.companyInfo?.phoneCode} {data?.companyInfo?.phone}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <p className="text-sm text-gray-500">Address</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.legalAddress}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Electronic Invoice Code (SDI)</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyInfo?.electronicInvoiceCode}</p>
                </div>
              </div>

              {/* Document Upload Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {data?.companyInfo?.visuraSocietaria.map((file, index) => (
                  <div key={`document-${index}`} className="border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-2">Visura Societaria</h3>
                    <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                      {/* Optional: show file name */}
                      <iframe
                        src={`${appConfig.FILE_URL}${file}`}
                        className="w-full h-full"
                        title={`Document Copy ${index}`}
                      />
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <button
                        onClick={() => handleDownloadContract(file)}
                        className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100"
                      >
                        <Download size={14} /> Download
                      </button>
                      <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                        <FileUp size={14} /> Update
                      </button>
                    </div>
                  </div>
                ))}

                {/* Fiscal Code (CF) */}
                {/* <div className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-2">Fiscal Code (CF)</h3>
                  <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                 
                    <span className="text-gray-400">+</span>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                      <Download size={14} /> Download
                    </button>
                    <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                      <FileUp size={14} /> Update
                    </button>
                  </div>
                </div> */}
              </div>
            </div>
          )}

          {activeTab === 'legal' && (
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {/* Personal Info Fields */}
                <div>
                  <p className="text-sm text-gray-500">First Name</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyOwner?.firstName}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Last Name</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyOwner?.lastName}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Gender</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyOwner?.gender}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Birth Date</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyOwner?.birthDate}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Birth Place</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyOwner?.birthPlace}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Fiscal Code</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyOwner?.fiscalCode}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Document Type</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyOwner?.documentType}</p>
                </div>

                <div>
                  <p className="text-sm text-gray-500">Document Number</p>
                  <p className="flex items-center gap-2 font-medium">{data?.companyOwner?.documentNumber}</p>
                </div>
              </div>

              {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
           
                <div className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-2">Identity Document</h3>
                  <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                    <span className="text-gray-400">+</span>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                      <Download size={14} /> Download
                    </button>
                    <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                      <FileUp size={14} /> Update
                    </button>
                  </div>
                </div>

        
                <div className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-2">Fiscal Code (CF)</h3>
                  <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                    <span className="text-gray-400">+</span>
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                      <Download size={14} /> Download
                    </button>
                    <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                      <FileUp size={14} /> Update
                    </button>
                  </div>
                </div>
              </div> */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
                {/* Document Copy */}
                {data?.companyOwner?.documentCopy.map((file, index) => (
                  <div key={`document-${index}`} className="border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-2">Document Copy</h3>
                    <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                      {/* Optional: show file name */}
                      <iframe
                        src={`${appConfig.FILE_URL}${file}`}
                        className="w-full h-full"
                        title={`Document Copy ${index}`}
                      />
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <button
                        onClick={() => handleDownloadContract(file)}
                        className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100"
                      >
                        <Download size={14} /> Download
                      </button>
                      <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                        <FileUp size={14} /> Update
                      </button>
                    </div>
                  </div>
                ))}

                {/* Fiscal Code Copy */}
                {data?.companyOwner?.fiscalCodeCopy.map((file, index) => (
                  <div key={`fiscal-${index}`} className="border rounded-lg p-4">
                    <h3 className="text-sm font-semibold mb-2">Fiscal Code (CF)</h3>
                    <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                      <iframe
                        src={`${appConfig.FILE_URL}${file}`}
                        className="w-full h-full"
                        title={`Document Copy ${index}`}
                      />
                    </div>
                    <div className="flex justify-between items-center mt-4">
                      <button
                        onClick={() => handleDownloadContract(file)}
                        className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100"
                      >
                        <Download size={14} /> Download
                      </button>
                      <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                        <FileUp size={14} /> Update
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="bg-white border rounded-lg shadow-sm mt-5">
          <div className="flex items-center gap-2 text-2xl font-bold p-5 mb-2">
            <FileText className="text-purple-600" /> Owner Information
          </div>

          <div className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {/* Company Info Fields */}
              <div>
                <p className="text-sm text-gray-500">First Name</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.firstName}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Last Name</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.lastName}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Gender</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.gender}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Birth Date</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.birthDate}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Birth Place</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.birthPlace}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Fiscal Code</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.fiscalCode}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Email</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.email}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Document Type</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.documentType}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Phone</p>
                <p className="flex items-center gap-2 font-medium">
                  {data?.privateOwner?.phoneCode} {data?.privateOwner?.phone}
                </p>
              </div>

              <div className="md:col-span-2">
                <p className="text-sm text-gray-500">Address</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.address}</p>
              </div>

              <div>
                <p className="text-sm text-gray-500">Document Number</p>
                <p className="flex items-center gap-2 font-medium">{data?.privateOwner?.documentNumber}</p>
              </div>
            </div>

            {/* Document Upload Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
              {/* Document Copy */}
              {data?.privateOwner?.documentCopy.map((file, index) => (
                <div key={`document-${index}`} className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-2">Document Copy</h3>
                  <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                    {/* Optional: show file name */}
                    <iframe
                      src={`${appConfig.FILE_URL}${file}`}
                      className="w-full h-full"
                      title={`Document Copy ${index}`}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() => handleDownloadContract(file)}
                      className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100"
                    >
                      <Download size={14} /> Download
                    </button>
                    <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                      <FileUp size={14} /> Update
                    </button>
                  </div>
                </div>
              ))}

              {/* Fiscal Code Copy */}
              {data?.privateOwner?.fiscalCodeCopy.map((file, index) => (
                <div key={`fiscal-${index}`} className="border rounded-lg p-4">
                  <h3 className="text-sm font-semibold mb-2">Fiscal Code (CF)</h3>
                  <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                    <iframe
                      src={`${appConfig.FILE_URL}${file}`}
                      className="w-full h-full"
                      title={`Document Copy ${index}`}
                    />
                  </div>
                  <div className="flex justify-between items-center mt-4">
                    <button
                      onClick={() => handleDownloadContract(file)}
                      className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100"
                    >
                      <Download size={14} /> Download
                    </button>
                    <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                      <FileUp size={14} /> Update
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="w-full mt-3 p-6 bg-white border  rounded-lg shadow-sm">
        <div className="flex justify-between items-center mb-2">
          <div className="flex items-center gap-2">
            <Building2 className="text-indigo-500" size={24} />
            <h1 className="text-2xl font-bold text-gray-900">Properties</h1>
          </div>
          <button className="flex items-center gap-2 bg-indigo-500 text-white py-3 px-4 rounded-lg hover:bg-indigo-600 transition-colors">
            <FileText size={20} />
            <span>Add New Property Contract URL</span>
          </button>
        </div>

        <p className="text-gray-500 mb-6">Properties managed under this contract</p>

        <div className="space-y-4">
          {/* Sea Breeze Property */}
          {data?.properties?.map((property) => (
            <div
              onClick={() => navigate('/signed/property_detail', { state: { property, data } })}
              className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
            >
              <div className="flex justify-between items-center">
                <div className="flex gap-4">
                  <div className="w-24 h-20 rounded overflow-hidden">
                    <img
                      src={
                        property?.mainPhoto
                          ? appConfig.FILE_URL + property?.mainPhoto
                          : 'https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcQsMMUke4L3aBu8jImsZhwYBwzFA3z0HOnRgg&s'
                      }
                      alt="Sea Breeze property"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{property.name}</h2>
                    <div className="flex items-center text-gray-500 mt-1">
                      <MapPin size={16} className="mr-1" />
                      <span>{property.address}</span>
                    </div>
                    <div className="flex items-center gap-4 text-gray-500 mt-2">
                      <span>{property.size} m²</span>
                      <span>•</span>
                      <span>Floor {property.floor}</span>
                      <span>•</span>
                      <span>Max guests: {property.manualMaxGuests}</span>
                    </div>
                  </div>
                </div>
                <button className="text-gray-400 hover:text-gray-700">
                  <ArrowRight size={24} />
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="bg-white border rounded-lg shadow-sm mt-5">
        <div className="flex items-center gap-2 text-2xl font-bold p-5 mb-2">
          <FileText className="text-purple-600" /> Bank Information & Signature Sample
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Company Info Fields */}
            <div>
              <p className="text-sm text-gray-500">Account Holder</p>
              <p className="flex items-center gap-2 font-medium">{data?.bankInfo?.accountHolder}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">Bank</p>
              <p className="flex items-center gap-2 font-medium">{data?.bankInfo?.bank}</p>
            </div>

            <div>
              <p className="text-sm text-gray-500">IBAN</p>
              <p className="flex items-center gap-2 font-medium">{data?.bankInfo?.iban}</p>
            </div>
          </div>

          {/* Document Upload Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
            <div className="border rounded-lg p-4">
              <h3 className="text-sm font-semibold mb-2">Signature Sample</h3>
              <div className="h-40 bg-gray-100 rounded flex items-center justify-center">
                <img src={data?.paymentInfo?.ownerSignature} className="w-full h-full" title="Signature Sample" />
              </div>
              <div className="flex justify-between items-center mt-4">
                <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                  <Download size={14} /> Download
                </button>
                <button className="flex items-center gap-2 text-sm px-3 py-1 border rounded-md hover:bg-gray-100">
                  <FileUp size={14} /> Update
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full mt-4 p-6 bg-white rounded-lg shadow-sm border border-amber-200">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Admin correction</h2>
            <p className="text-sm text-amber-700">
              NON dovresti modificare nulla qui salvo errori reali. La prima correzione conserva i dati originali firmati nello storico.
            </p>
            {data?.adminCorrection?.updatedAt && (
              <p className="text-xs text-gray-500 mt-2">
                Last temporary correction: {new Date(data.adminCorrection.updatedAt).toLocaleString()}
              </p>
            )}
          </div>
          {!editMode && (
            <button
              onClick={handleEnableEditMode}
              className="flex items-center gap-2 px-4 py-2 border border-amber-300 text-amber-800 rounded-md hover:bg-amber-50"
            >
              <Pencil size={16} /> Edit mode
            </button>
          )}
        </div>

        {editMode && (
          <div className="mt-6 space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <label className="text-sm font-medium text-gray-700">
                Signed mode
                <select
                  value={adminEdit.signedContractMode}
                  onChange={(e) => updateAdminEditField('signedContractMode', e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-md p-2"
                >
                  <option value="standard">Standard</option>
                  <option value="allInclusive">All Inclusive</option>
                </select>
              </label>
              <label className="text-sm font-medium text-gray-700">
                Display name
                <input value={adminEdit.name} onChange={(e) => updateAdminEditField('name', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md p-2" />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Language
                <input value={adminEdit.language} onChange={(e) => updateAdminEditField('language', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md p-2" />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Duration
                <input value={adminEdit.contractDuration} onChange={(e) => updateAdminEditField('contractDuration', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md p-2" />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Percentage
                <input type="number" value={adminEdit.percentage} onChange={(e) => updateAdminEditField('percentage', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md p-2" />
              </label>
              <label className="text-sm font-medium text-gray-700">
                Cancellation notice
                <input value={adminEdit.terminationNotice} onChange={(e) => updateAdminEditField('terminationNotice', e.target.value)} className="mt-1 w-full border border-gray-300 rounded-md p-2" />
              </label>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {[
                ['companyInfo', 'Company info'],
                ['companyOwner', 'Company owner / legal representative'],
                ['privateOwner', 'Private owner'],
                ['bankInfo', 'Bank info'],
              ].map(([field, label]) => (
                <label key={field} className="text-sm font-medium text-gray-700">
                  {label}
                  <textarea
                    value={adminEdit[field]}
                    onChange={(e) => updateAdminEditField(field, e.target.value)}
                    className="mt-1 w-full h-52 font-mono text-xs border border-gray-300 rounded-md p-3"
                  />
                </label>
              ))}
            </div>

            <label className="text-sm font-medium text-gray-700 block">
              Properties
              <textarea
                value={adminEdit.properties}
                onChange={(e) => updateAdminEditField('properties', e.target.value)}
                className="mt-1 w-full h-72 font-mono text-xs border border-gray-300 rounded-md p-3"
              />
            </label>

            <label className="text-sm font-medium text-gray-700 block">
              Correction note
              <textarea
                value={adminEdit.note}
                onChange={(e) => updateAdminEditField('note', e.target.value)}
                className="mt-1 w-full h-24 border border-gray-300 rounded-md p-3"
                placeholder="Why was this correction needed?"
              />
            </label>

            <div className="flex flex-wrap gap-3">
              <button
                onClick={handleSaveAdminCorrection}
                disabled={savingAdminEdit}
                className="px-5 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-60"
              >
                {savingAdminEdit ? 'Saving...' : 'Save temporary correction'}
              </button>
              <button
                onClick={() => {
                  setAdminEdit(buildAdminEditState(data));
                  setEditMode(false);
                }}
                className="px-5 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      <div className="w-full mt-4  p-6 bg-white rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Notes</h2>
        <p className="text-gray-500 mb-4">Add notes about this owner</p>

        <div className="mb-4">
          <textarea
            value={ownerNotes}
            onChange={(e) => setOwnerNotes(e.target.value)}
            className="w-full h-36 p-4 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
            placeholder="Add notes about this owner..."
          />
        </div>

        <button
          className="bg-indigo-500 text-white py-3 px-8 rounded-lg hover:bg-indigo-600 transition-colors font-medium"
          onClick={() => handleSaveNotes()}
        >
          Save Notes
        </button>
      </div>
    </div>
  );
}
